import { useEffect, useMemo, useRef, useState } from 'react';
import { Api } from '../application/panelApi';
import { LaneIcon } from '../components/LaneIcon';
import { confirmDialog } from '../components/ModalHost';
import { toast } from '../components/ToastHost';
import {
  TASK_STATUS_LABEL,
  autopilotSteps,
  type AutopilotConfig,
  type AutopilotState,
} from '../features/autopilot';
import { clLiveStats } from '../features/clusters';
import type { PfTask } from '../domain/positions';
import { errText, nf } from '../lib/format';
import { useApp } from '../store/AppContext';
import { TargetNodes, TaskFormModal } from './TasksPage';

const noop = () => undefined;

const engineOf = (t: PfTask): 'bing' | 'google' =>
  t.searchEngine === 'google' ? 'google' : 'bing';

function okRemainingToday(t: PfTask): number {
  if (t.status === 'not_found') return 0;
  const daily = Math.floor(Number(t.dailyClicks || t.perDay) || 0);
  const okToday = Math.max(0, (t.clicksOk || 0) - Math.floor(Number(t.quotaBaselineOk) || 0));
  return Math.max(0, daily - okToday);
}

const STATUS_ICON: Record<string, string> = {
  run: 'M10 8.5v7l5.5-3.5z',
  pause: 'M10 8.5v7M14 8.5v7',
  stop: 'M9 9h6v6H9z',
  not_found: 'M14 11a3 3 0 1 1-6 0a3 3 0 1 1 6 0M13.2 13.2L16 16',
};

function TaskStatusIcon({ status, label }: { status: string; label: string }) {
  const known = STATUS_ICON[status] ? status : 'stop';
  const text = label.charAt(0).toUpperCase() + label.slice(1);
  return (
    <span className={`lp-st ${known}`} title={text} role="img" aria-label={text}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path
          d={STATUS_ICON[known]}
          fill={known === 'run' || known === 'stop' ? 'currentColor' : 'none'}
        />
      </svg>
    </span>
  );
}

export function AutopilotPanel() {
  const {
    autopilot: rawAutopilot,
    tasks: rawTasks,
    clusters,
    ui,
    controlMode,
    setAutopilot,
    refreshAutopilot,
    refreshTasks,
    refreshClusters,
    refreshProxy,
    proxyPools,
    scenarios,
    audit,
    goto,
  } = useApp();
  const p = rawAutopilot as AutopilotState | null;
  const tasks = rawTasks as PfTask[];
  const running = p?.status === 'running';
  const eng = ui.searchEngine === 'google' ? 'google' : 'bing';
  useEffect(() => {
    void Promise.all([
      refreshTasks().catch(noop),
      refreshAutopilot(),
      controlMode === 'control' ? refreshClusters().catch(noop) : undefined,
    ]);
    const timer = window.setInterval(() => void refreshAutopilot(), 10_000);
    return () => window.clearInterval(timer);
  }, [controlMode, refreshAutopilot, refreshClusters, refreshTasks]);

  const cfg: AutopilotConfig = {
    taskIds: p?.config?.taskIds ?? [],
    emptyTaskIds: p?.config?.emptyTaskIds ?? [],
  };
  const cfgKey = `${cfg.taskIds.join('|')}#${cfg.emptyTaskIds.join('|')}`;
  const [selected, setSelected] = useState<Set<string>>(() => new Set(cfg.taskIds));
  const [emptyIds, setEmptyIds] = useState<Set<string>>(() => new Set(cfg.emptyTaskIds));
  const selectedRef = useRef(selected);
  const savedSelection = useRef(new Set(cfg.taskIds));
  const saveQueue = useRef(Promise.resolve());
  const saveVersion = useRef(0);
  useEffect(() => {
    const next = new Set(cfg.taskIds);
    selectedRef.current = next;
    savedSelection.current = next;
    setSelected(next);
    setEmptyIds(new Set(cfg.emptyTaskIds));
  }, [cfgKey]);

  const visibleTasks = useMemo(() => tasks.filter((t) => engineOf(t) === eng), [tasks, eng]);
  const visibleChecked = visibleTasks.filter((t) => selected.has(t.id));
  const allMode = visibleChecked.length === 0;
  const isPicked = (id: string) => allMode || selected.has(id);
  const pickTask = (id: string) => {
    const current = selectedRef.current;
    const hasVisibleSelection = visibleTasks.some((t) => current.has(t.id));
    const next = hasVisibleSelection ? new Set(current) : new Set([...current, ...visibleTasks.map((t) => t.id)]);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selectedRef.current = next;
    setSelected(next);
    saveSelection(next);
  };

  const [settingsFor, setSettingsFor] = useState<PfTask | null>(null);
  const [settingsEmpty, setSettingsEmpty] = useState(false);
  const openSettings = (t: PfTask) => {
    setSettingsFor(t);
    setSettingsEmpty(emptyIds.has(t.id));
    void refreshProxy().catch(noop);
  };
  const [createLane, setCreateLane] = useState<'pf' | 'neuro' | null>(null);
  const [cloneFrom, setCloneFrom] = useState<PfTask | null>(null);
  const openCreate = (lane: 'pf' | 'neuro') => {
    setCloneFrom(null);
    setCreateLane(lane);
    setSettingsEmpty(false);
    void refreshProxy().catch(noop);
  };
  const cloneTask = (t: PfTask) => {
    setSettingsFor(null);
    setCloneFrom({ ...t, site: `${t.site} — копия`, keywords: [...(t.keywords || [])] });
    setCreateLane(t.lane === 'neuro' ? 'neuro' : 'pf');
    setSettingsEmpty(emptyIds.has(t.id));
  };
  const deleteTask = (t: PfTask) => {
    confirmDialog(
      'Удалить цель?',
      `Цель «${t.site}» будет удалена вместе со статистикой. Восстановить её нельзя.`,
      () => {
        void (async () => {
          try {
            await Api.tasks.remove(t.id);
            audit('Удалил задачу', t.site);
            setSettingsFor(null);
            await refreshTasks().catch(noop);
            toast(`Цель ${t.site} удалена.`, 'ok');
          } catch (err) {
            toast(errText(err), 'err');
          }
        })();
      },
      { yes: 'Удалить' },
    );
  };
  const emptyOpt = (
    <div className="lp-launch-opt">
      <button
        type="button"
        role="switch"
        aria-checked={settingsEmpty}
        className={settingsEmpty ? 'on' : undefined}
        onClick={() => setSettingsEmpty((v) => !v)}
      >
        <span className="lp-gear-check" aria-hidden="true">
          {settingsEmpty ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          ) : null}
        </span>
        <span>
          <b>Пустые профили</b>
          <small>Клик сразу новым профилем, без подготовки. Профиль удаляется после клика.</small>
        </span>
      </button>
    </div>
  );
  const proxyTags = useMemo(() => {
    const list = proxyPools.map((pool) => pool.tag).filter((v): v is string => Boolean(v));
    return list.length ? list : ['DEFAULT'];
  }, [proxyPools]);

  const onSettingsSaved = async (savedId?: string) => {
    await refreshTasks().catch(noop);
    if (!savedId || emptyIds.has(savedId) === settingsEmpty) return;
    const next = new Set(emptyIds);
    if (settingsEmpty) next.add(savedId);
    else next.delete(savedId);
    setEmptyIds(next);
    try {
      applyResponse(
        await Api.autopilot.save({
          taskIds: cfg.taskIds,
          emptyTaskIds: [...next].filter((id) => id === savedId || tasks.some((x) => x.id === id)),
        }),
      );
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const taskGroups = (
    [
      {
        id: 'pf',
        lane: 'search',
        title: 'Поисковик',
        unit: 'кликов в день',
        quotaUnit: 'кликов',
        formLane: 'pf',
        tasks: visibleTasks.filter((t) => t.lane !== 'neuro'),
      },
      {
        id: 'ai',
        lane: 'ai',
        title: 'AI поиск',
        unit: 'сообщений в день',
        quotaUnit: 'сообщений',
        formLane: 'neuro',
        tasks: visibleTasks.filter((t) => t.lane === 'neuro'),
      },
    ] as const
  ).map((g) => {
    const picked = allMode ? g.tasks : g.tasks.filter((t) => selected.has(t.id));
    return {
      ...g,
      active: picked.length,
      quota: picked.reduce((sum, t) => sum + okRemainingToday(t), 0),
    };
  });


  const readForm = (selection = selected): AutopilotConfig => {
    const visibleIds = visibleTasks.map((t) => t.id);
    const kept = [...selection].filter((id) => {
      const t = tasks.find((x) => x.id === id);
      return Boolean(t) && engineOf(t as PfTask) !== eng;
    });
    const checked = visibleIds.filter((id) => selection.has(id));
    const taskIds =
      visibleIds.length && checked.length === 0
        ? kept.length === 0
          ? []
          : [...new Set([...kept, ...visibleIds])]
        : [...new Set([...kept, ...checked])];
    const emptyTaskIds = [...emptyIds].filter((id) => tasks.some((t) => t.id === id));
    return { taskIds, emptyTaskIds };
  };

  const applyResponse = (data: { autopilot?: AutopilotState; pipeline?: AutopilotState }) => {
    setAutopilot(data.autopilot ?? data.pipeline ?? null);
  };

  const saveSelection = (selection: Set<string>) => {
    const config = readForm(selection);
    const version = ++saveVersion.current;
    saveQueue.current = saveQueue.current.then(async () => {
      try {
        const data = await Api.autopilot.save(config);
        savedSelection.current = new Set(config.taskIds);
        if (version === saveVersion.current) applyResponse(data);
      } catch (err) {
        if (version === saveVersion.current) {
          const previous = new Set(savedSelection.current);
          selectedRef.current = previous;
          setSelected(previous);
          toast(errText(err), 'err');
          void refreshAutopilot().catch(noop);
        }
      }
    });
  };

  const start = async () => {
    try {
      if (!tasks.length) {
        toast('Сначала создайте хотя бы одну цель', 'wa');
        return;
      }
      const data = await Api.autopilot.start(readForm());
      applyResponse(data);
      const phase = ((data.autopilot ?? data.pipeline) as AutopilotState | undefined)?.phase;
      toast(
        phase === 'target'
          ? 'Автопилот включён — запускаю задачи'
          : phase === 'farm'
            ? 'Автопилот включён — сначала прогрев'
            : 'Автопилот включён',
        'ok',
      );
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const stop = () => {
    if (!running) return;
    confirmDialog(
      'Выключить автопилот?',
      'Задачи уйдут на паузу, фарм остановится (уже идущие сессии прогрева доиграют, до ~40 мин). Включить снова можно в любой момент.',
      () => {
        void (async () => {
          try {
            const data = await Api.autopilot.stop();
            applyResponse(data);
            toast('Автопилот выключен — задачи на паузе, фарм остановлен', 'ok');
          } catch (err) {
            toast(errText(err), 'err');
          }
        })();
      },
      { yes: 'Выключить' },
    );
  };

  const warming = clusters.reduce((a, c) => a + clLiveStats(c).profilesWarming, 0);
  const ready = p?.readyNow || 0;
  const need = p?.nextTaskNeed || 0;
  const step = p ? autopilotSteps(p).find((s) => s.active)?.id : undefined;

  const view: { tone: 'off' | 'run' | 'done' | 'err'; label: string; title: string } = !p
    ? { tone: 'off', label: 'Загрузка', title: 'Загружаю состояние…' }
    : p.status === 'error'
      ? { tone: 'err', label: 'Ошибка', title: 'Запуск остановился с ошибкой' }
      : !running
        ? { tone: 'off', label: 'Автозапуск остановлен', title: 'Сейчас автозапуск не работает' }
        : step === 'target'
          ? { tone: 'run', label: 'Работает', title: 'Задачи в процессе выполнения' }
          : step === 'loop'
            ? { tone: 'done', label: 'Работает', title: 'План на сегодня выполнен' }
            : step === 'farm-ready'
              ? { tone: 'run', label: 'Работает', title: 'Профилей хватает — переходим к задачам' }
              : { tone: 'run', label: 'Работает', title: 'Готовим профили' };
  const since =
    running && p?.runStartedAt
      ? ` с ${new Date(p.runStartedAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
      : '';
  const readyPct = need ? Math.min(100, (ready / need) * 100) : 0;

  return (
    <div className={`lp-hero ${view.tone}`}>
      <div className="lp-hero-top">
        <div className="lp-hero-text">
          <span className={`lp-state ${view.tone}`}>
            <i />
            {view.label}
            {since}
          </span>
          <h2>{view.title}</h2>
        </div>
        {running ? (
          <button type="button" className="btn lp-stop" id="plStop" onClick={stop}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
            </svg>
            Остановить
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary lp-go"
            id="plStart"
            disabled={!p}
            onClick={() => void start()}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z"
                fill="currentColor"
              />
            </svg>
            Запустить
          </button>
        )}
      </div>

      {p?.lastError ? <div className="lp-error">{p.lastError}</div> : null}

      <div className="lp-lanes">
        {taskGroups.map((g) => (
          <section key={g.id} className={`lp-lane ${g.id}`}>
            <h3 className="lp-lane-title">
              <LaneIcon kind={g.lane} />
              {g.title}
            </h3>
            <div className="lp-lane-facts">
              <div className="lp-fact">
                <span className="lp-fact-k">Квота на сегодня</span>
                <span className="lp-fact-v">
                  <b>{nf.format(g.quota)}</b>
                </span>
              </div>
              <div className="lp-fact">
                <span className="lp-fact-k">Выбрано задач</span>
                <span className="lp-fact-v">
                  <b>{nf.format(g.active)}</b> из {nf.format(g.tasks.length)}
                </span>
              </div>
            </div>
          </section>
        ))}
      </div>

      {controlMode === 'control' ? (
        <div className="lp-row lp-target">
          <span className="lp-k">Ноды для кликов по задачам</span>
          <TargetNodes />
        </div>
      ) : null}

      <div className="lp-profiles">
        <div className="lp-fact">
          <span className="lp-fact-k">Количество полных профилей</span>
          <span className="lp-fact-v">
            <b>{nf.format(ready)}</b>
            {need ? ` из ${nf.format(need)}` : ''}
          </span>
          {need ? (
            <span className="lp-bar">
              <i style={{ width: `${readyPct.toFixed(1)}%` }} />
            </span>
          ) : null}
          {warming ? <span className="lp-fact-sub">ещё {nf.format(warming)} готовятся</span> : null}
        </div>
        {!visibleTasks.length ? <div className="lp-profiles-actions"><button type="button" className="btn lp-toggle" onClick={() => goto('search')}>Создать задачу</button></div> : null}
      </div>

        <div className="lp-editor">
          <div className="lp-groups" id="plTasks">
            {taskGroups.map((g) => (
              <section key={g.id} className={`lp-group ${g.id}`}>
                <div className="lp-group-head">
                  <h3>
                    <LaneIcon kind={g.lane} />
                    {g.title}
                  </h3>
                  <span className="lp-count">
                    {nf.format(g.active)} из {nf.format(g.tasks.length)}
                  </span>
                </div>
                {g.tasks.length ? (
                  <ul className="lp-tasks">
                    {g.tasks.map((t) => {
                      const perDay = Math.floor(Number(t.dailyClicks || t.perDay) || 0);
                      const st = TASK_STATUS_LABEL[t.status] || t.status;
                      return (
                        <li
                          key={t.id}
                          data-task={t.id}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isPicked(t.id)}
                          className={`lp-task${isPicked(t.id) ? ' sel' : ''}${emptyIds.has(t.id) ? ' empty' : ''}`}
                          onClick={() => pickTask(t.id)}
                          onKeyDown={(e) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              pickTask(t.id);
                            }
                          }}
                        >
                          <span className="lp-task-main">
                            <TaskStatusIcon status={t.status} label={st} />
                            <span className="lp-task-text">
                              <span className="lp-task-site">{t.site || t.id}</span>
                              <span className="lp-task-meta">
                                {nf.format(perDay)} {g.unit}
                                {emptyIds.has(t.id) ? ' · пустые профили' : ''}
                              </span>
                            </span>
                          </span>
                          <span
                            className="lp-gear-wrap"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className={`lp-gear${settingsFor?.id === t.id ? ' open' : ''}`}
                              aria-label={`Настройки цели ${t.site || t.id}`}
                              title="Настройки цели"
                              aria-haspopup="dialog"
                              onClick={() => openSettings(t)}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M15 12a3 3 0 1 1-6 0a3 3 0 1 1 6 0ZM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z"
                                />
                              </svg>
                            </button>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                <button
                  type="button"
                  className="lp-add"
                  aria-label={g.formLane === 'neuro' ? 'Добавить сайт' : 'Создать задачу'}
                  title={g.formLane === 'neuro' ? 'Добавить сайт' : 'Создать задачу'}
                  onClick={() => openCreate(g.formLane)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
              </section>
            ))}
          </div>
        </div>

      {createLane ? (
        <TaskFormModal
          key={cloneFrom ? `clone:${cloneFrom.id}` : `new:${createLane}`}
          task={cloneFrom}
          mode={cloneFrom ? 'clone' : 'create'}
          lane={createLane}
          proxyTags={proxyTags}
          scenarios={scenarios}
          defaultEngine={eng}
          audit={audit}
          onClose={() => setCreateLane(null)}
          onSaved={onSettingsSaved}
          extra={emptyOpt}
        />
      ) : null}
      {settingsFor ? (
        <TaskFormModal
          key={settingsFor.id}
          task={settingsFor}
          mode="edit"
          lane={settingsFor.lane === 'neuro' ? 'neuro' : 'pf'}
          proxyTags={proxyTags}
          scenarios={scenarios}
          defaultEngine={eng}
          audit={audit}
          onClose={() => setSettingsFor(null)}
          onSaved={onSettingsSaved}
          extra={emptyOpt}
          footerStart={
            <div className="tf-foot-start">
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => deleteTask(settingsFor)}
              >
                Удалить цель
              </button>
              <button type="button" className="btn" onClick={() => cloneTask(settingsFor)}>
                Создать копию
              </button>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
